import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumAssetType } from '@wix/ambassador-premium-billing-and-payments/types';
import { Box, Divider, Text } from '@wix/design-system';
import { useUnknownChargeContext } from '../../providers/UnknownChargeProvider';
import {
  makeDateFormatter,
  usePremiumCurrencyFormatter,
} from '@wix/premium-react-text';
import { paymentMethodDisplay } from '@wix/billing-client-common';
import { useAppContext } from '../../providers/AppContextProvider';
import { useResponsiveComponents } from '../../hooks/useResponsiveComponents';
import { useViewport } from '../../hooks/useViewport';
import { useBiLogger } from '../../providers/BiLoggerProvider';
import { useResultsPagePhase1 } from '../../hooks/useResultsPagePhase1';

export const DetailsList: React.FC = () => {
  const {
    chargeDetails: {
      amount,
      date,
      description,
      quantity,
      domainName,
      paymentMethod: { label, type },
      subscriptionPeriod,
      invoiceNumber,
      currencyCode,
      service,
      accountEmailMasked,
    },
  } = useUnknownChargeContext();
  const { biLogger } = useBiLogger();
  const {
    appContext: { locale },
  } = useAppContext();
  const { format: dateFormatter } = makeDateFormatter(locale, 'shortMonth');
  const { shortName, identifier } = paymentMethodDisplay(
    { label, paymentBrand: type },
    locale,
  );
  const [formatCurrency] = usePremiumCurrencyFormatter({ currencyCode });
  const [Responsive] = useResponsiveComponents();
  const { currentBreakpoint } = useViewport();
  const phase1Enabled = useResultsPagePhase1();
  const { t } = useTranslation();

  useEffect(() => {
    biLogger.chargeDetailsView({
      amount: String(amount),
      invoiceNumber,
      paymentMethod: Number(label),
      productTypeName: service,
      transactionDate: new Date(date).getTime(),
    });
  }, []);
  return (
    <>
      {phase1Enabled && accountEmailMasked && (
        // Render label and email as fixed siblings rather than going through
        // Responsive.ChargeDetail's <Trans>-based template. The translation
        // currently provisioned in production is "Associated Wix Account"
        // (no colon, no `{{email}}` placeholder), and any similarly
        // incomplete translation in another locale would otherwise hide
        // the masked email entirely. Decoupling the email value from the
        // i18n template guarantees it renders even when the template is
        // missing punctuation or the placeholder.
        //
        // Mirror the responsive layout used by ChargeDetail (inline span)
        // and MobileChargeDetail (block div on its own line) so the email
        // wraps to a new row on mobile/tablet like every other detail.
        currentBreakpoint.isMobile || currentBreakpoint.isTablet ? (
          <Text dataHook="charge-details__associated-account" secondary>
            <Box marginBottom="SP1" display="block">
              {t(
                'charge-locator.results-page.field-name.associated-account',
                'Associated Wix account',
              )}
              :
              <Text tagName="div" secondary={false} weight="normal">
                {accountEmailMasked}
              </Text>
            </Box>
          </Text>
        ) : (
          <Text dataHook="charge-details__associated-account" secondary>
            {t(
              'charge-locator.results-page.field-name.associated-account',
              'Associated Wix account',
            )}
            {': '}
            <Text tagName="span" secondary={false} weight="normal">
              {accountEmailMasked}
            </Text>
          </Text>
        )
      )}
      {service === PremiumAssetType.PLAN ? (
        <Responsive.ChargeDetail
          transkey="service-premium-plan"
          keyValues={{ planName: description }}
        />
      ) : (
        <Responsive.ChargeDetail
          transkey="service"
          keyValues={{ productType: description }}
        />
      )}
      {service === PremiumAssetType.MAILBOX && (
        <Responsive.ChargeDetail
          transkey="business-email-details"
          keyValues={{ count: quantity, domainName }}
        />
      )}
      <Responsive.ChargeDetail
        transkey="amount"
        keyValues={{ amount: formatCurrency(amount) }}
      />

      <Responsive.ChargeDetail
        transkey="date"
        keyValues={{ eventDate: date && dateFormatter(date) }}
      />
      <Responsive.ChargeDetail
        transkey="payment-method"
        keyValues={{
          paymentMethod: shortName,
          identifier,
        }}
      />
      {subscriptionPeriod && (
        <Responsive.ChargeDetail
          transkey="subscription-period"
          keyValues={{
            periodStart: dateFormatter(subscriptionPeriod?.startTime!),
            periodEnd: dateFormatter(subscriptionPeriod?.endTime!),
          }}
        />
      )}
      <Responsive.ChargeDetail
        transkey="invoice-number"
        keyValues={{
          invoiceNumber,
        }}
      />
      <Box marginTop={currentBreakpoint.isMobile ? 'SP5' : 'SP8'}>
        <Divider />
      </Box>
    </>
  );
};
